<template>
  <div>
    <IdmAuth>
      <div id="appWrapper">
        <div class="app-header">
          <IdmHeader
            :userName="$auth.user.details.firstName + ' ' + $auth.user.details.lastName"
            :userMail="$auth.user.details.email"
            :userID="'' + $auth.user.details.accountId"
            :method="signOut"
            help
            :identicalHeader="true"
            :settingsLink="false"
            :studioLink="''"
          ></IdmHeader>
          <a
            v-if="studioHomeHref"
            class="studio-logo-link"
            :href="studioHomeHref"
            aria-label="Studio"
            data-test="header-logo-link"
          ></a>
        </div>

        <div v-if="requestErrorPopupIsShown" class="popup-bg">
          <IdmPopup
            align="left"
            :title="requestError.message"
            :paragraphs="[
              `If you believe you have received this message in error, please contact the Idomoo account owner or <a href='mailto:support@idomoo.com'>support@idomoo.com</a> and request a new access link.`,
            ]"
            :errorTitle="requestError.code"
            :errorText="requestError.description"
          >
            <IdmButton :btnType="'secondary'" text="Close" @click="closeErrorPopup" data-test="error-popup-close-button" />
          </IdmPopup>
        </div>

        <router-view />
      </div>
    </IdmAuth>
  </div>
</template>

<script>
import { provideApolloClient } from '@vue/apollo-composable';
import { clearSignedOut, markSignedOut } from '@/helper/sceneLoginUrl.js';
import { useErrorsStore } from '@/stores/errorsStore';

export default {
  name: 'App',

  data() {
    return {
      userDetails: null,
      href: null,
    };
  },
  computed: {
    errorsStore() {
      return useErrorsStore();
    },
    requestErrorPopupIsShown() {
      return this.errorsStore.requestErrorPopupIsShown;
    },
    requestError() {
      return this.errorsStore.error;
    },
    studioLink() {
      return import.meta.env.VITE_APP_STUDIO_LINK || 'https://studio.idomoo.com/';
    },
    studioHomeHref() {
      return this.studioLink;
    },
  },
  watch: {
    userDetails(val) {
      if (!this.href[1]) {
        this.phUserEntered();
      } else {
        this.phLoggedIn();
      }
    },
    'requestError.description'(newVal) {
      if (newVal) this.errorsStore.showRequestErrorPopup();
    },
    '$auth.initialized'(newVal) {
      if (newVal) {
        this.provideApollo();
        clearSignedOut();
      }
    },
    '$auth.tokenOutdated'(newVal) {
      if (newVal) {
        this.phTimeoutReached();
      }
    },
    '$auth.user.details.email'(newVal) {
      if(!import.meta.env.VITE_APP_DISABLE_POSTHOG){
        try {
          this.$posthog.people.set({ email: newVal });
          this.$posthog.identify(newVal);
        } catch (error) {
          console.warn('PostHog tracking failed:', error);
        }
      }
    },
  },

  methods: {
    signOut() {
      this.phUserLoggedOut();
      markSignedOut();
      this.$auth.logout(false, { userInitiated: true });
    },
    closeErrorPopup() {
      this.errorsStore.hideRequestErrorPopup();
      this.$router.go(this.$router.currentRoute);
    },
    provideApollo() {
      const apolloClient = this.$auth.apolloClient;
      console.log('apolloClient version => ', apolloClient.version);
      provideApolloClient(apolloClient);
      this.userDetails = this.$auth.user.details;
      console.log('apollo provided');
    },
    phLoggedIn() {
      this.sendPostHogEvent("User logged in")
    },
    phUserEntered() {
      this.sendPostHogEvent("User entered")
    },
    phUserLoggedOut() {
      this.sendPostHogEvent("User logged out")
    },
    phTimeoutReached() {
      this.sendPostHogEvent("Timeout reached")
    },
    sendPostHogEvent(eventName) {
      const properties = {
        timestamp: Date.now(),
        email: this.userDetails.email,
        pv_account_id: this.userDetails.accountId,
        event_origin: 'idm_custom_event',
        storyboard_id: -1,
        landingpage_id: -1
      }

      if (!import.meta.env.VITE_APP_DISABLE_POSTHOG) {
        try {
          this.$posthog.capture(
            eventName,
            properties
          );
        } catch (error) {
          console.warn('PostHog tracking failed:', error);
        }
      }
    }
  },
  mounted() {
    this.href = window.location.href.split('#');
    if (this.$auth.initialized) {
      this.provideApollo();
    }
  },
};
</script>

<style scoped>
.app-header {
  position: relative;
}

.studio-logo-link {
  position: absolute;
  z-index: 2;
  top: 0;
  left: 0;
  width: 152px;
  height: 56px;
}

.app-header:has(.studio-logo-link:hover) :deep(.idm-header > svg.logo) {
  filter: invert(35%) sepia(100%) saturate(30%) hue-rotate(149deg) brightness(100%) contrast(107%);
}
</style>
